import React, { useEffect, useMemo, useState } from 'react';
import { GameWorldProvider } from './GameWorldContext';
import TileMap from './TileMap';
import { createDefaultMap } from './tiles';
import GameAPI from '../Game/GameAPI';
import {
  isCollectingAction,
} from '../Game/Gathering/gathering';
import { resolveApiErrorMessage } from '../../Api';
import Button from '../../ui/Button';

const DEFAULT_DESTINATION_OPTIONS = {
  stopWithinDistance: 0,
  arrivalAction: 'RESTING',
};

function derivePositionFromUser(user, fallbackWidth = 100) {
  if (!user) return { x: 0, y: 0 };
  const { lastLocationIndex, lastLocationX, lastLocationY } = user;
  if (Number.isFinite(lastLocationX) && Number.isFinite(lastLocationY)) {
    return { x: lastLocationX, y: lastLocationY };
  }
  if (!Number.isFinite(lastLocationIndex)) {
    return { x: 0, y: 0 };
  }
  return {
    x: lastLocationIndex % fallbackWidth,
    y: Math.floor(lastLocationIndex / fallbackWidth),
  };
}

function isExplicitlyTrue(value) {
  return value === true || String(value).toLowerCase() === 'true';
}

export function resolveTravelAvailability(user) {
  const currentAction = String(
    user?.currentAction ?? user?.CurrentAction ?? '',
  ).toUpperCase();
  const hasCombat = Boolean(user?.userCombat ?? user?.UserCombat)
    || ['FIGHTING', 'FLEEING'].includes(currentAction);
  const allowed = !hasCombat && currentAction !== 'DEAD';
  return {
    allowed,
    reason: allowed
      ? ''
      : currentAction === 'DEAD'
        ? 'World actions are unavailable while dead.'
        : 'World actions are unavailable during combat.',
  };
}

export function resolveCampEntrance(map, region, baseCamp) {
  const isStartRegion = isExplicitlyTrue(
    region?.canBeStartRegion ?? region?.CanBeStartRegion,
  );
  const regionId = String(region?.id ?? region?.Id ?? '').trim();
  const route = (baseCamp?.routes || []).find(candidate => (
    candidate.regionId === regionId
  ));
  const costItemId = String(
    route?.costItemId
      ?? region?.campRouteCostItemId
      ?? region?.CampRouteCostItemId
      ?? '',
  ).trim();
  const costQuantity = Number(
    route?.requiredQuantity
      ?? region?.campRouteCostQuantity
      ?? region?.CampRouteCostQuantity
      ?? 0,
  );
  if (!isStartRegion && (!costItemId || costQuantity <= 0)) return null;

  const xValue = map?.campEntranceX ?? map?.CampEntranceX;
  const yValue = map?.campEntranceY ?? map?.CampEntranceY;
  const x = Number(xValue);
  const y = Number(yValue);
  if (
    xValue !== undefined
    && xValue !== null
    && yValue !== undefined
    && yValue !== null
    && Number.isInteger(x)
    && Number.isInteger(y)
  ) {
    return {
      x,
      y,
      regionId,
      regionName: route?.regionName
        || String(region?.name ?? region?.Name ?? 'this region'),
      installed: isStartRegion || Boolean(route?.installed),
      costItemId: route?.costItemId || costItemId,
      costItemName: route?.costItemName || costItemId,
      requiredQuantity: Number(route?.requiredQuantity) || costQuantity,
      availableInPack: Number(route?.availableInPack) || 0,
    };
  }

  if (!isStartRegion) return null;

  const legacyXValue = map?.spawnLocationX ?? map?.SpawnLocationX;
  const legacyYValue = map?.spawnLocationY ?? map?.SpawnLocationY;
  const legacyX = Number(legacyXValue);
  const legacyY = Number(legacyYValue);
  return legacyXValue !== undefined
    && legacyXValue !== null
    && legacyYValue !== undefined
    && legacyYValue !== null
    && Number.isInteger(legacyX)
    && Number.isInteger(legacyY)
    ? {
      x: legacyX,
      y: legacyY,
      regionId,
      regionName: route?.regionName
        || String(region?.name ?? region?.Name ?? 'this region'),
      installed: true,
      costItemId: '',
      costItemName: '',
      requiredQuantity: 0,
      availableInPack: 0,
    }
    : null;
}

export function resolveRegionExits(map) {
  const exits = map?.exits ?? map?.Exits;
  if (!Array.isArray(exits)) return [];

  return exits.map(exit => {
    const key = String(exit?.key ?? exit?.Key ?? '').trim();
    const xValue = exit?.x ?? exit?.X;
    const yValue = exit?.y ?? exit?.Y;
    const x = Number(xValue);
    const y = Number(yValue);
    if (
      !key
      || xValue === undefined
      || xValue === null
      || yValue === undefined
      || yValue === null
      || !Number.isInteger(x)
      || !Number.isInteger(y)
    ) return null;

    const enabledValue = exit?.enabled ?? exit?.Enabled;
    return {
      key,
      label: String(exit?.label ?? exit?.Label ?? key).trim() || key,
      x,
      y,
      direction: String(
        exit?.direction ?? exit?.Direction ?? '',
      ).trim().toLowerCase(),
      targetRegionId: String(
        exit?.targetRegionId ?? exit?.TargetRegionId ?? '',
      ).trim(),
      targetExitKey: String(
        exit?.targetExitKey ?? exit?.TargetExitKey ?? '',
      ).trim(),
      enabled: enabledValue === undefined
        ? true
        : isExplicitlyTrue(enabledValue),
    };
  }).filter(Boolean);
}

export function resolveScavengeRadius(user, mapWidth, mapHeight) {
  const stats = user?.statsCached ?? user?.StatsCached ?? {};
  const key = Object.keys(stats).find(name => (
    name.toLowerCase().replace(/[^a-z0-9]/g, '') === 'junkradar'
  ));
  const stat = stats[key];
  const to = Number(stat?.valueTo ?? stat?.ValueTo);
  const from = Number(stat?.valueFrom ?? stat?.ValueFrom);
  const value = to === 0 && from !== 0 ? from : to;
  const bonus = Number.isFinite(value)
    ? Math.floor(Math.max(0, value))
    : 0;
  const mapMaximum = Math.max(0, mapWidth - 1)
    + Math.max(0, mapHeight - 1);
  return Math.min(mapMaximum, 2 + bonus);
}

function GameTileExperienceComponent({
  baseCamp,
  user,
  region,
  avatarConfig,
  avatarSelection,
  draggedItem,
  onDropInventoryItem,
  onActivateCampEntrance,
  onActivateRegionExit,
  onCampEntranceChange,
  onOpenGroundLoot,
  onOpenWorldLoot,
  onOpenStarterCache,
  onStartChampionEncounter,
  onRetryResourceNodes,
  regionWorldState,
  resourceNodesError = '',
  resourceNodesLoading = false,
  playerLevel = 1,
  scavengePreviewVisible = false,
  starterCache,
  queuedWorldIntent,
  onRequestWorldIntent,
  onConsumeWorldIntent,
  onClearWorldIntent,
}) {
  const [mapData, setMapData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mapError, setMapError] = useState('');
  const [mapLoadAttempt, setMapLoadAttempt] = useState(0);
  const [tileTypes, setTileTypes] = useState([]);
  const [detailTypes, setDetailTypes] = useState([]);
  const [movementTarget, setMovementTarget] = useState(null);
  const [movementPending, setMovementPending] = useState(false);
  const [movementError, setMovementError] = useState('');
  const [championInteractionError, setChampionInteractionError] = useState('');
  const [activatingChampionId, setActivatingChampionId] = useState('');

  const fallbackMap = useMemo(() => createDefaultMap(100, 100), []);
  const campEntrance = useMemo(
    () => resolveCampEntrance(mapData, region, baseCamp),
    [baseCamp, mapData, region],
  );
  const regionExits = useMemo(
    () => resolveRegionExits(mapData),
    [mapData],
  );
  const collectingLocked = isCollectingAction(user?.currentAction);
  const travelAvailability = useMemo(
    () => resolveTravelAvailability(user),
    [user],
  );

  useEffect(() => {
    onCampEntranceChange?.(campEntrance);
  }, [campEntrance, onCampEntranceChange]);

  useEffect(() => {
    if (user?.movementTargetX === null || user?.movementTargetX === undefined
      || user?.movementTargetY === null || user?.movementTargetY === undefined) {
      setMovementTarget(null);
      return;
    }
    const x = Number(user?.movementTargetX);
    const y = Number(user?.movementTargetY);
    setMovementTarget(Number.isInteger(x) && Number.isInteger(y) ? { x, y } : null);
  }, [user?.movementTargetX, user?.movementTargetY]);

  useEffect(() => {
    let isMounted = true;

    async function loadPalettes() {
      try {
        const [tiles, details] = await Promise.all([
          GameAPI.fetchTileTypes(),
          GameAPI.fetchTileDecorations(),
        ]);
        if (isMounted) {
          setTileTypes(Array.isArray(tiles) ? tiles : []);
          setDetailTypes(Array.isArray(details) ? details : []);
        }
      } catch (e) {
        if (isMounted) {
          setTileTypes([]);
          setDetailTypes([]);
        }
      }
    }

    loadPalettes();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      if (!region?.id) {
        setMapData(null);
        setMapError('');
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setMapError('');
      setMapData(null);
      try {
        const result = await GameAPI.fetchRegionMap(region.id);
        if (isMounted && result) {
          setMapData(result);
        } else if (isMounted) {
          setMapData(null);
          setMapError(
            'The region map could not be loaded. Fallback terrain is shown.',
          );
        }
      } catch (e) {
        console.warn('Falling back to default tilemap', e);
        if (isMounted) {
          setMapData(null);
          setMapError(
            'The region map could not be loaded. Fallback terrain is shown.',
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    load();

    return () => {
      isMounted = false;
    };
  }, [mapLoadAttempt, region?.id]);

  const activeMap = mapData || fallbackMap;
  const mapWidth = activeMap.width || activeMap.Width || fallbackMap.width;
  const mapHeight = activeMap.height || activeMap.Height || fallbackMap.height;
  const scavengeRadius = resolveScavengeRadius(user, mapWidth, mapHeight);
  const lastLocationIndex = user?.lastLocationIndex;
  const lastLocationX = user?.lastLocationX;
  const lastLocationY = user?.lastLocationY;
  const initialPosition = useMemo(
    () => derivePositionFromUser({
      lastLocationIndex,
      lastLocationX,
      lastLocationY,
    }, mapWidth),
    [
      lastLocationIndex,
      lastLocationX,
      lastLocationY,
      mapWidth,
    ],
  );

  const selectMovementTarget = async (x, y, options = {}) => {
    if (movementPending) return null;
    if (collectingLocked) {
      return null;
    }
    if (user?.currentRunId && user?.starterLoadoutReady === false) {
      setMovementError(resolveApiErrorMessage({
        code: 'STARTER_LOADOUT_REQUIRED',
      }));
      return null;
    }
    setMovementPending(true);
    setMovementError('');
    try {
      const response = await GameAPI.SetMovementTarget(x, y, {
        ...DEFAULT_DESTINATION_OPTIONS,
        ...options,
      });
      const result = await response.json();
      setMovementTarget(Number.isInteger(result.targetX) && Number.isInteger(result.targetY)
        ? { x: result.targetX, y: result.targetY }
        : null);
      return result;
    } catch (error) {
      setMovementError(resolveApiErrorMessage(
        error,
        'This tile cannot be reached right now. Choose another destination.',
      ));
      return null;
    } finally {
      setMovementPending(false);
    }
  };

  const activateChampion = async champion => {
    if (
      !champion?.placementId
      || activatingChampionId
      || !travelAvailability.allowed
    ) {
      return null;
    }
    if (!onStartChampionEncounter) {
      setChampionInteractionError(
        'Champion encounters are not available right now.',
      );
      return null;
    }

    setActivatingChampionId(champion.placementId);
    setChampionInteractionError('');
    try {
      return await onStartChampionEncounter(champion);
    } catch (error) {
      setChampionInteractionError(resolveApiErrorMessage(
        error,
        `${champion.name || 'The champion'} could not be challenged. Try again.`,
      ));
      return null;
    } finally {
      setActivatingChampionId('');
    }
  };

  return (
    <div
      aria-busy={isLoading || resourceNodesLoading || undefined}
      className="gameworld__container"
    >
      <div className="gameworld__notice-stack">
        {isLoading ? (
          <div
            aria-live="polite"
            className="gameworld__loading"
            role="status"
          >
            Loading region map…
          </div>
        ) : null}
        {mapError ? (
          <div className="gameworld__map-error" role="alert">
            <span>{mapError}</span>
            <Button
              onClick={() => setMapLoadAttempt(attempt => attempt + 1)}
              variant="secondary"
            >
              Retry map
            </Button>
          </div>
        ) : null}
        {movementPending ? (
          <div className="gameworld__movement-status" role="status">
            Calculating route…
          </div>
        ) : null}
        {movementError ? (
          <div className="gameworld__movement-error" role="alert">
            {movementError}
          </div>
        ) : null}
        {!collectingLocked && !travelAvailability.allowed ? (
          <div className="gameworld__collection-lock" role="status">
            {travelAvailability.reason}
          </div>
        ) : null}
        {resourceNodesLoading ? (
          <div
            aria-live="polite"
            className="gameworld__resource-status"
            role="status"
          >
            Refreshing resource nodes…
          </div>
        ) : null}
        {resourceNodesError ? (
          <div className="gameworld__resource-load-error" role="alert">
            <span>{resourceNodesError}</span>
            {typeof onRetryResourceNodes === 'function' ? (
              <Button
                disabled={resourceNodesLoading}
                onClick={() => {
                  Promise.resolve(onRetryResourceNodes()).catch(error => {
                    console.error('Failed to retry resource nodes', error);
                  });
                }}
                variant="secondary"
              >
                Retry resources
              </Button>
            ) : null}
          </div>
        ) : null}
        {championInteractionError ? (
          <div className="gameworld__resource-error" role="alert">
            {championInteractionError}
          </div>
        ) : null}
      </div>
      <GameWorldProvider
        map={activeMap}
        initialPlayerPosition={initialPosition}
        tileset={tileTypes}
        decorations={detailTypes}
        movementPath={user?.movementPath}
        movementTarget={movementTarget}
        onSelectMovementTarget={selectMovementTarget}
        movementPending={movementPending}
        avatarConfig={avatarConfig}
        avatarSelection={avatarSelection}
        campEntrance={campEntrance}
        regionExits={regionExits}
        onActivateCampEntrance={onActivateCampEntrance}
        onActivateRegionExit={onActivateRegionExit}
        travelAllowed={travelAvailability.allowed}
        travelUnavailableReason={travelAvailability.reason}
        playerAction={user?.currentAction}
        playerLevel={playerLevel}
        scavengeRadius={scavengePreviewVisible ? scavengeRadius : 0}
        draggedItem={draggedItem}
        onDropInventoryItem={onDropInventoryItem}
        onOpenGroundLoot={onOpenGroundLoot}
        onOpenWorldLoot={onOpenWorldLoot}
        onOpenStarterCache={onOpenStarterCache}
        onActivateChampion={activateChampion}
        championBusyPlacementId={activatingChampionId}
        queuedWorldIntent={queuedWorldIntent}
        onRequestWorldIntent={onRequestWorldIntent}
        onConsumeWorldIntent={onConsumeWorldIntent}
        onClearWorldIntent={onClearWorldIntent}
        regionWorldState={regionWorldState}
        starterCache={starterCache}
      >
        <TileMap />
      </GameWorldProvider>
    </div>
  );
}

const GameTileExperience = React.memo(GameTileExperienceComponent);

export default GameTileExperience;
